import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '@shared/shared.module';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { HdBodygraphCenter, HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import { HdBodygraphRenderStateFactory } from '../bodygraph/hd-bodygraph-render-state.factory';
import { HdMindFieldsComponent } from '../bodygraph/advanced-properties/hd-mind-fields.component';
import { buildHdMindCenterOptions, buildHdMindGateOptions } from '../bodygraph/advanced-properties/hd-mind-conditioners';
import { joinHdMomentExtra, parseHdMomentExtra } from '../bodygraph/advanced-properties/hd-moment-extra';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdChartCreateFormComponent } from './hd-chart-create-form.component';
import { HdColorPickerComponent } from './hd-color-picker.component';
import { HdDotPickerComponent, HdDotPickerOption } from './hd-dot-picker.component';
import { HdIconPickerComponent } from './hd-icon-picker.component';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { HdChartCalculationResult, HdChartDocumentResult, HdChartUpdateInput, chartTypeForPreset } from './hd-chart-library.models';
import { buildChartEditFormValue, carryUserHandwork } from './hd-chart-edit.utils';
import {
  HD_MIND_MOMENT_CATEGORY,
  HD_MOMENT_CATEGORIES,
  HD_MOMENT_EMOTIONS,
  HD_MOMENT_MAX_EMOTIONS,
  HD_MOOD_COLORS,
  HD_MOOD_VALUES,
  HD_VITALITY_ICONS,
  HD_VITALITY_VALUES,
  hdEmotionLabelKey,
  hdMomentCategoryLabelKey,
  hdMoodLabelKey,
  hdSignpostCounterpart,
  hdVitalityLabelKey,
  joinHdMomentEmotions,
  parseHdMomentEmotions,
  toggleHdMomentEmotion,
} from '../bodygraph/advanced-properties/hd-moment-fields';

@Component({
  selector: 'hd-chart-edit-dialog',
  standalone: true,
  imports: [SharedModule, TranslocoModule, FormsModule, HdChartCreateFormComponent, HdIconPickerComponent, HdColorPickerComponent, HdDotPickerComponent, HdMindFieldsComponent],
  templateUrl: './hd-chart-edit-dialog.component.html',
  styleUrl: './hd-chart-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartEditDialogComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Te bewerken chart (geladen document); null = dialog gesloten. */
  readonly target = model<HdChartDocumentResult | null>(null);
  /** Huidige "My chart"-status van de chart (de detail-DTO draagt die niet; de lijst wel). */
  readonly isMyChart = input(false);
  /**
   * "My chart"-checkbox tonen? De tijdlijn-/sectie-edit kent de status niet
   * (relaties-feed draagt hem niet) en verbergt hem daarom (finetune 2026-09-21).
   */
  readonly showMyChart = input(true);
  /** Klaar om te persisteren: naam + document (origineel of vers herberekend) + huidige metadata. */
  readonly saved = output<HdChartUpdateInput>();

  /** Checkbox-staat; prefillt bij elke (her)opening vanuit de input. */
  protected readonly myChartChecked = signal(false);

  /** Custom icoon (kale FA-naam); null = het charttype-icoon (beslispunt 7 notable moments). */
  protected readonly icon = signal<string | null>(null);

  // Momentdetails (spec levenstijdlijn §2.10): alleen getoond én bewaard voor
  // momentcharts (preset met transit-blok); prefillt uit de huidige metadata.
  protected readonly color = signal<string | null>(null);
  protected readonly category = signal<string | null>(null);
  protected readonly mood = signal<number | null>(null);
  protected readonly vitality = signal<number | null>(null);
  protected readonly emotionKeys = signal<readonly string[]>([]);
  // Mind-notitie (spec 2026-10-22-mind-logboek §4): centrum en poort bewerkbaar,
  // de conditioneerder blijft zoals bewaard (opnieuw koppelen zit niet in de editdialoog).
  protected readonly mindCenter = signal<HdBodygraphCenter | null>(null);
  protected readonly mindGate = signal<number | null>(null);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  /** Het moment heeft een mind-blok (gedachte); de oude categorie `mind` telt ook. */
  private readonly mindPresent = signal(false);
  protected readonly mindActive = computed(() => this.momentDetailsAvailable() && (this.mindPresent() || this.category() === HD_MIND_MOMENT_CATEGORY));
  protected readonly mindCenterOptions = computed(() => {
    const preset = this.target()?.preset;
    return preset ? buildHdMindCenterOptions(preset.configuration, this.renderStateFactory) : [];
  });
  protected readonly mindGateOptions = computed(() => {
    const preset = this.target()?.preset;
    const center = this.mindCenter();
    return preset && center ? buildHdMindGateOptions(preset.configuration, center) : [];
  });

  protected readonly categories = HD_MOMENT_CATEGORIES;
  protected readonly vitalityValues = HD_VITALITY_VALUES;
  protected readonly vitalityIcons = HD_VITALITY_ICONS;
  protected readonly maxEmotions = HD_MOMENT_MAX_EMOTIONS;
  protected readonly emotionOptions = HD_MOMENT_EMOTIONS;

  /** Stemming als gedeelde dot-picker (zelfde bolletjes-selector als de kleuren). */
  protected readonly moodOptions = computed<readonly HdDotPickerOption[]>(() =>
    HD_MOOD_VALUES.map(mood => ({
      id: String(mood),
      color: HD_MOOD_COLORS[mood] ?? '',
      label: this.detailLabels().mood[mood],
    })));

  protected readonly moodDotValue = computed(() => {
    const mood = this.mood();
    return mood != null ? String(mood) : null;
  });

  protected onMoodPicked(id: string | null): void {
    this.mood.set(id == null ? null : Number(id));
  }

  /** Momentdetails horen bij gelogde momenten (transit-blok). */
  protected readonly momentDetailsAvailable = computed(() => this.target()?.preset.transit != null);

  constructor() {
    effect(() => {
      const target = this.target();
      if (target) {
        this.myChartChecked.set(this.isMyChart());
        this.icon.set(target.meta.icon ?? null);
        this.color.set(target.meta.color ?? null);
        this.category.set(target.meta.category ?? null);
        this.mood.set(target.meta.mood ?? null);
        this.vitality.set(target.meta.vitality ?? null);
        this.emotionKeys.set(parseHdMomentEmotions(target.meta.emotions));
        const mind = parseHdMomentExtra(target.meta.momentExtraJson).mind;
        this.mindPresent.set(mind != null);
        this.mindCenter.set(mind?.center ?? null);
        this.mindGate.set(mind?.gate ?? null);
      }
    });
  }

  protected toggleCategory(category: string): void {
    this.category.set(this.category() === category ? null : category);
  }

  protected toggleVitality(vitality: number): void {
    this.vitality.set(this.vitality() === vitality ? null : vitality);
  }

  /** Max 3; binnen een signpost-paar vervangt de tegenhanger (gedeelde helper). */
  protected toggleEmotion(key: string): void {
    this.emotionKeys.set(toggleHdMomentEmotion(this.emotionKeys(), key));
  }

  /** Chip uitgegrijsd op het maximum, behalve als hij een geselecteerde tegenhanger vervangt. */
  protected emotionDisabled(key: string): boolean {
    const current = this.emotionKeys();
    if (current.includes(key) || current.length < HD_MOMENT_MAX_EMOTIONS) return false;
    const counterpart = hdSignpostCounterpart(key);
    return counterpart == null || !current.includes(counterpart);
  }

  protected readonly initial = computed(() => {
    const target = this.target();
    return target ? buildChartEditFormValue(target) : undefined;
  });

  /**
   * Cycle-/composite-charts zijn vergrendeld op naamwijziging: een herberekening
   * uit de (natale bron-)geboortegegevens zou het cycle-/composite-document
   * door een gewone natal chart vervangen.
   */
  protected readonly renameOnly = computed(() => {
    const target = this.target();
    return target != null && chartTypeForPreset(target.preset) !== PlatformChartType.Standard;
  });

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.chart.editDialog.title'),
      close: translate('actions.close'),
      myChartLabel: translate('humanDesign.chartManager.myChart.checkboxLabel'),
      iconLabel: translate('humanDesign.chart.editDialog.iconLabel'),
      categoryLabel: translate('humanDesign.momentFields.categoryLabel'),
      colorLabel: translate('humanDesign.momentFields.colorLabel'),
      moodLabel: translate('humanDesign.momentFields.moodLabel'),
      vitalityLabel: translate('humanDesign.momentFields.vitalityLabel'),
      emotionsLabel: translate('humanDesign.momentFields.emotionsLabel'),
    };
  });

  /** Vertaalde labels voor de detailvelden, in de content-taal (zelfde patroon als text()). */
  protected readonly detailLabels = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      category: Object.fromEntries(HD_MOMENT_CATEGORIES.map(category =>
        [category, translate(hdMomentCategoryLabelKey(category))])) as Record<string, string>,
      mood: Object.fromEntries(HD_MOOD_VALUES.map(mood =>
        [mood, translate(hdMoodLabelKey(mood))])) as Record<number, string>,
      vitality: Object.fromEntries(HD_VITALITY_VALUES.map(vitality =>
        [vitality, translate(hdVitalityLabelKey(vitality))])) as Record<number, string>,
      emotion: Object.fromEntries(HD_MOMENT_EMOTIONS.map(emotion =>
        [emotion.key, translate(hdEmotionLabelKey(emotion.key))])) as Record<string, string>,
    };
  });

  protected onOpenChange(open: boolean): void {
    if (!open) this.target.set(null);
  }

  /** Geboortegegevens gewijzigd → het vers berekende document bewaren, mét het handwerk (pins e.d.) van het origineel. */
  onCalculated(result: HdChartCalculationResult): void {
    // Vangnet naast de formulier-vergrendeling: een vergrendelde chart mag zijn
    // document nooit verliezen — degradeer naar een naamwijziging.
    if (this.renameOnly()) {
      this.onRenamedOnly(result.name);
      return;
    }
    const original = this.target()?.preset;
    this.emitSaved(result.name, original ? carryUserHandwork(result.preset, original) : result.preset);
  }

  /** Enkel de naam gewijzigd → het originele document behouden (naam merget de service). */
  onRenamedOnly(name: string): void {
    const target = this.target();
    if (!target) return;
    this.emitSaved(name, target.preset);
  }

  private emitSaved(name: string, preset: HdBodygraphChartPreset): void {
    const target = this.target();
    if (!target?.meta.id) return;
    // Momentdetails alleen als volledige set meesturen op momentcharts;
    // andere charts behouden hun huidige (lege) waarden (key blijft dan weg).
    const details = this.momentDetailsAvailable()
      ? {
          color: this.color(),
          category: this.category(),
          mood: this.mood(),
          vitality: this.vitality(),
          emotions: joinHdMomentEmotions(this.emotionKeys()),
          momentExtraJson: this.buildMomentExtraJson(target.meta.momentExtraJson),
        }
      : undefined;
    this.saved.emit({
      id: target.meta.id,
      name,
      preset,
      current: target.meta,
      isMyChart: this.myChartChecked(),
      icon: this.icon(),
      ...(details ? { details } : {}),
    });
    this.target.set(null);
  }

  /**
   * Het extra-blok: bij een mind-notitie het centrum en de poort uit de dialoog
   * (de bewaarde conditioneerder blijft), anders het bestaande blok ongewijzigd.
   */
  private buildMomentExtraJson(current: string | null | undefined): string | null {
    const extra = parseHdMomentExtra(current);
    if (!this.mindActive()) return joinHdMomentExtra(extra);
    const center = this.mindCenter();
    const gate = this.mindGate();
    const { center: _center, gate: _gate, ...rest } = extra.mind ?? {};
    return joinHdMomentExtra({
      ...extra,
      mind: { ...rest, ...(center != null ? { center } : {}), ...(gate != null ? { gate } : {}) },
    });
  }
}
